import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  anonymousClient,
  inferAdditionalFields,
} from 'better-auth/client/plugins'
import { createAuthClient } from 'better-auth/react'
import { useContext, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { completeAccountExit, prepareAccountEntry } from './account-exit'
import {
  publishAccountEnded,
  requestAccountSessionRevalidation,
} from './account-transition-signal'
import {
  passwordResetRedirectTo,
  returnTargetNavigation,
  safeReturnTarget,
  signInUrlReturningTo,
} from './return-target'
import { isArenaHost } from '@/lib/arena'
import { isLobbyEnabled, postAuthLandingPath } from '@/lib/lobby'

import { AuthContext } from '@/context/auth'
import { API_URL } from '@/lib/constants'
import { useI18n } from '@/lib/i18n'
import { normalizeSignupUsername } from '@/lib/signup-username'
import { browserNotificationRuntime } from '@/lib/browser-notification-runtime'
import { useTRPC } from '@/lib/trpc'
import {
  detachWebPushBeforeAccountExit,
  sanitizeWebPushBeforeAccountEntry,
} from '@/lib/web-push-client'

function defaultLanding(): string {
  return postAuthLandingPath(isArenaHost(), isLobbyEnabled())
}

export const authClient = createAuthClient({
  baseURL: API_URL,
  plugins: [
    inferAdditionalFields({
      user: {
        username: {
          type: 'string',
          required: false,
        },
      },
    }),
    anonymousClient(),
  ],
})

function isUserInfoQuery(query: { queryKey: readonly unknown[] }) {
  const key = query.queryKey
  const path = Array.isArray(key[0]) ? key[0] : key
  return (
    Array.isArray(path)
    && path.includes('userRouter')
    && path.includes('getUserInfo')
  )
}

function getSignUpFailureReason(message: string | undefined): string {
  if (message === 'Failed to create user') {
    return 'Username is already taken. Please choose another.'
  }
  return message || 'Please check your information and try again.'
}

export function useAuth() {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const { userInfo, authResolved, browserSessionId } = useContext(AuthContext)
  const { t } = useI18n()
  const [isLoading, setIsLoading] = useState(false)
  const navigateAfterAccountEntry = (
    rawTarget: string | null,
    fallback: string,
  ) => {
    const destination = returnTargetNavigation(
      rawTarget,
      fallback,
      window.location.pathname,
    )
    if (destination.fullNavigation)
      window.location.assign(destination.to)
    else navigate(destination.to)
  }
  const deactivatePushSessionMutation = useMutation(
    trpc.webPushRouter.deactivateSession.mutationOptions(),
  )
  const deactivatePushSessionRef = useRef(
    deactivatePushSessionMutation.mutateAsync,
  )
  deactivatePushSessionRef.current = deactivatePushSessionMutation.mutateAsync
  const detachCurrentAccountPush = async (
    expectedUserId: string,
    expectedSessionId: string | null,
  ): Promise<boolean> => {
    const result = await detachWebPushBeforeAccountExit(
      expectedUserId,
      expectedSessionId,
      {
        deactivateSession: input => deactivatePushSessionRef.current(input),
      },
      browserNotificationRuntime,
    )
    return result.safeToClearAccountIdentity
  }
  const isAnonymous = userInfo?.isAnonymous === true
  const user = isAnonymous ? null : userInfo
  const clearAccountState = async () => {
    await queryClient.cancelQueries()
    queryClient.clear()
  }
  const prepareForAccountEntry = async () => {
    const current = await authClient.getSession()
    if (current.error) {
      await clearAccountState()
      return false
    }
    const currentUser = current.data?.user as
      | { id: string, isAnonymous?: boolean }
      | undefined
    const registeredUserId
      = currentUser && !currentUser.isAnonymous ? currentUser.id : null
    return prepareAccountEntry(
      registeredUserId,
      {
        detachPush: detachCurrentAccountPush,
        sanitizePush: () =>
          sanitizeWebPushBeforeAccountEntry(browserNotificationRuntime),
        clearAccountState,
        notifyAccountEnded: publishAccountEnded,
      },
      current.data?.session.id ?? null,
    )
  }

  const signInAnonymously = async (): Promise<boolean> => {
    if (userInfo)
      return true

    try {
      const existingSession = await authClient.getSession()
      if (!existingSession.data?.user) {
        if (!(await prepareForAccountEntry())) {
          return false
        }
        const { error } = await authClient.signIn.anonymous()
        if (error)
          return false
      }

      if (existingSession.data?.user)
        await clearAccountState()
      await queryClient.invalidateQueries({ predicate: isUserInfoQuery })
      return true
    }
    catch {
      return false
    }
  }

  const signIn = async ({
    email,
    password,
  }: {
    email: string
    password: string
  }) => {
    setIsLoading(true)
    try {
      if (!(await prepareForAccountEntry())) {
        toast.error(t('pushNotifications.accountEntryCleanupError'))
        setIsLoading(false)
        return
      }
      const { error } = await authClient.signIn.email({ email, password })
      if (error) {
        toast.error(`Sign in failed: ${error.message}`)
      }
      else {
        if (!(await requestAccountSessionRevalidation())) {
          toast.error(t('pushNotifications.accountEntryVerificationError'))
          setIsLoading(false)
          return
        }
        toast.success('Sign in successful!')
        await queryClient.invalidateQueries({
          predicate: isUserInfoQuery,
        })
        navigateAfterAccountEntry(
          searchParams.get('redirect'),
          defaultLanding(),
        )
      }
    }
    catch {
      toast.error(
        'Sign in failed. Please check your credentials and try again.',
      )
    }
    setIsLoading(false)
  }

  const registerAccount = async ({
    email,
    password,
    username,
  }: {
    email: string
    password: string
    username: string
  }): Promise<{ ok: boolean }> => {
    setIsLoading(true)
    try {
      if (!(await prepareForAccountEntry())) {
        toast.error(t('pushNotifications.accountEntryCleanupError'))
        return { ok: false }
      }
      const normalizedUsername = normalizeSignupUsername(username)
      const { error } = await authClient.signUp.email({
        email,
        name: normalizedUsername,
        password,
        username: normalizedUsername,
      })
      if (error) {
        toast.error(`Sign up failed: ${getSignUpFailureReason(error.message)}`)
        return { ok: false }
      }
      if (!(await requestAccountSessionRevalidation())) {
        toast.error(t('pushNotifications.accountEntryVerificationError'))
        return { ok: false }
      }
      toast.success('Sign up successful!')
      await queryClient.invalidateQueries({ predicate: isUserInfoQuery })
      return { ok: true }
    }
    catch {
      toast.error(
        'Sign up failed. Please check your credentials and try again.',
      )
      return { ok: false }
    }
    finally {
      setIsLoading(false)
    }
  }

  const signUp = async (args: {
    email: string
    password: string
    username: string
  }) => {
    const { ok } = await registerAccount(args)
    if (!ok)
      return
    navigateAfterAccountEntry(searchParams.get('redirect'), defaultLanding())
  }

  const signOut = async () => {
    const result = await completeAccountExit(
      user?.id ?? null,
      {
        detachPush: detachCurrentAccountPush,
        signOut: () => authClient.signOut(),
        clearAccountState,
        notifyAccountEnded: publishAccountEnded,
      },
      browserSessionId,
    )
    if (!result.ok) {
      toast.error(
        t(
          result.failure === 'push-detach-failed'
            ? 'pushNotifications.accountExitDetachError'
            : 'pushNotifications.accountExitSignOutError',
        ),
      )
      return
    }
    if (result.authoritativeSessionEnded) {
      navigate('/')
      return
    }
    navigate('/')
  }

  const connectTwitter = async () => {
    setIsLoading(true)
    try {
      const { error } = await authClient.linkSocial({
        provider: 'twitter',
        callbackURL: '/account',
      })
      if (error) {
        toast.error('Failed to connect Twitter. Please try again.')
        setIsLoading(false)
      }
    }
    catch {
      toast.error('Failed to connect Twitter. Please try again.')
      setIsLoading(false)
    }
  }

  const changePassword = async ({
    currentPassword,
    newPassword,
  }: {
    currentPassword: string
    newPassword: string
  }) => {
    setIsLoading(true)
    try {
      const { error } = await authClient.changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: true,
      })
      if (error) {
        throw new Error(error.message)
      }
      const exitResult = await completeAccountExit(
        user?.id ?? null,
        {
          detachPush: detachCurrentAccountPush,
          signOut: () => authClient.signOut(),
          clearAccountState,
          notifyAccountEnded: publishAccountEnded,
        },
        browserSessionId,
      )
      if (!exitResult.ok) {
        throw new Error(
          t(
            exitResult.failure === 'push-detach-failed'
              ? 'pushNotifications.accountExitDetachError'
              : 'pushNotifications.accountExitSignOutError',
          ),
        )
      }
      if (exitResult.authoritativeSessionEnded) {
        navigate('/signin')
        return
      }
      navigate('/signin')
      toast.success('Password changed successfully!')
    }
    catch (err: unknown) {
      const message
        = err instanceof Error ? err.message : 'Failed to change password'
      toast.error(message)
      throw err
    }
    finally {
      setIsLoading(false)
    }
  }

  const forgetPassword = async ({ email }: { email: string }) => {
    setIsLoading(true)
    try {
      const { error } = await authClient.requestPasswordReset({
        email,
        redirectTo: passwordResetRedirectTo(
          window.location,
          searchParams.get('redirect'),
        ),
      })
      if (error) {
        console.log('Forget Password Error:', error)
        toast.error(
          `Failed to send reset email: ${error.message || JSON.stringify(error)}`,
        )
        return false
      }
      toast.success('Reset password email sent!')
      return true
    }
    catch {
      toast.error('Failed to send reset email. Please try again.')
      return false
    }
    finally {
      setIsLoading(false)
    }
  }

  const resetPassword = async ({ newPassword }: { newPassword: string }) => {
    setIsLoading(true)
    try {
      const token = searchParams.get('token')

      if (!token) {
        toast.error('No reset token found in URL')
        return false
      }

      const { error } = await authClient.resetPassword({
        newPassword,
        token,
      })
      if (error) {
        toast.error(`Failed to reset password: ${error.message}`)
        return false
      }
      toast.success('Password reset successfully!')
      const rawTarget = searchParams.get('redirect')
      navigate(
        rawTarget
          ? signInUrlReturningTo(safeReturnTarget(rawTarget, defaultLanding()))
          : '/signin',
      )
      return true
    }
    catch (err) {
      console.error('Reset password error:', err)
      toast.error('Failed to reset password. Please try again.')
      return false
    }
    finally {
      setIsLoading(false)
    }
  }

  return {
    signIn,
    registerAccount,
    signUp,
    signOut,
    connectTwitter,
    changePassword,
    forgetPassword,
    resetPassword,
    user,
    actor: userInfo,
    isAnonymous,
    signInAnonymously,
    isAuthenticated: !!user,
    authResolved,
    browserSessionId,
    isLoading,
    refetchProfile: () =>
      queryClient.invalidateQueries({
        predicate: isUserInfoQuery,
      }),
  }
}
